/**
 * Navigation labels. `hubs` and `ui` name the eight hubs, their tabs and the navigation UI (src/nav/hubs.ts);
 * `items` keeps the names of the former pages, which the Ctrl-K menu also matches.
 */
export const nav = {
  sections: {
    business: 'Business',
    compliance: 'Compliance',
    registry: 'Registry Ops',
    ops: 'Operations',
    settings: 'Integrations',
    admin: 'Administration',
  },
  groups: {
    compliance_vnnic: 'State reporting',
    compliance_icann: 'ICANN',
  },
  items: {
    '/domains': { label: 'Domains', description: 'Domain portfolio search, filters and bulk actions.' },
    '/customers': { label: 'Customers', description: 'Customer accounts, users and access.' },
    '/subjects': { label: 'Subjects', description: 'Legal registrants (individuals and organisations) behind domains.' },
    '/dossiers': { label: 'Dossiers', description: 'Domain dossiers — required for every managed domain (.vn, gTLD, new gTLD, ccTLD).' },
    '/documents': { label: 'Documents', description: 'Identity and registration documents attached to dossiers.' },
    '/quotes': { label: 'Quotes', description: 'Price quotes issued to customers.' },
    '/orders': { label: 'Orders', description: 'Orders, items, payments and fulfilment.' },
    '/billing': { label: 'Billing', description: 'Wallet balances, holds, payments and invoices at a glance.' },
    '/ledger': { label: 'Wallet / Ledger', description: 'Double-entry ledger and customer wallets. The ledger must always balance to zero.' },
    '/payments': { label: 'Payments', description: 'Payments, attempts, callbacks and refunds.' },
    '/invoices': { label: 'Invoices', description: 'Invoices and Vietnamese e-invoice issuance.' },
    '/pricing': { label: 'Pricing', description: 'Registry prices, price books, FX and tax rates.' },
    '/promotions': { label: 'Promotions', description: 'Promotion campaigns and their usage.' },
    '/auctions': { label: 'Auctions', description: 'Public and private backorder auctions: bids, results and history.' },
    '/backorders': { label: 'Backorders', description: 'Backorders, expected drops and drop-catch attempts.' },
    '/compliance/vnnic/dossier-sync': { label: 'Dossier Sync', description: 'Registrant dossier synchronisation with VNNIC for .vn names.' },
    '/compliance/vnnic/international-reports': { label: 'International Reports', description: 'Reporting of international TLD names held by Vietnamese registrants.' },
    '/compliance/vnnic/coverage': { label: 'Coverage / Mismatch', description: 'Which names are synced/reported, and where our records differ from VNNIC.' },
    '/compliance/vnnic/corrections': { label: 'Corrections', description: 'Dossier syncs VNNIC rejected or asked to correct, and report revisions.' },
    '/compliance/vnnic/post-operations': { label: 'After operations', description: 'What each TLD requires after a registry operation: registrar signature, dossier sync, change report.' },
    '/compliance/vnnic/registrar-signature': { label: 'Awaiting registrar signature', description: 'Dossiers waiting for the registrar\'s digital signature and the daily signing batches.' },
    '/compliance/vnnic/tracking-reports': { label: 'Daily tracking report', description: 'The daily .vn domain tracking report sent to VNNIC: requests and issued names per channel.' },
    '/compliance/icann/rdap': { label: 'RDAP', description: 'RDAP service switches, publication policy and lookup preview.' },
    '/compliance/icann/rde': { label: 'RDE', description: 'Registration data escrow deposits.' },
    '/compliance/icann/disclosure': { label: 'Disclosure', description: 'Registration data disclosure requests.' },
    '/compliance/icann/abuse': { label: 'Abuse', description: 'Abuse cases, evidence, law-enforcement requests and domain actions.' },
    '/compliance/icann/disputes': { label: 'Disputes', description: 'UDRP / URS, court orders and other disputes with their legal locks.' },
    '/compliance/cases': { label: 'Cases', description: 'Abuse, dispute and disclosure cases in one queue.' },
    '/compliance/legal-locks': { label: 'Legal Locks', description: 'Court, law-enforcement and registry locks on domains.' },
    '/registry/registries': { label: 'Registries', description: 'Registries, their zones, connections and health.' },
    '/registry/tlds': { label: 'TLDs', description: 'Top-level domains and their managed-domain counts.' },
    '/registry/epp': { label: 'EPP', description: 'EPP connection configuration of each registry.' },
    '/registry/sessions': { label: 'Sessions', description: 'EPP session pools and recent traffic.' },
    '/registry/transactions': { label: 'Registry Transactions', description: 'Every EPP command with clTRID / svTRID and outcome.' },
    '/registry/poll': { label: 'Poll', description: 'Registry poll message queue.' },
    '/registry/reconciliation': { label: 'Reconciliation', description: 'Resolving UNKNOWN registry outcomes and drift against registry state.' },
    '/ops/overview': { label: 'Overview', description: 'Key registrar health indicators.' },
    '/ops/services': { label: 'Services', description: 'Platform services and whether they are up.' },
    '/ops/registries': { label: 'Registry health', description: 'Registry connectivity, latency and error rates.' },
    '/ops/queues': { label: 'Queues', description: 'Job queues, events waiting to be delivered and in-flight workflows.' },
    '/ops/workflows': { label: 'Workflows', description: 'Workflow list and step history.' },
    '/ops/metrics': { label: 'Metrics', description: 'Query platform metrics at a point in time or over a range.' },
    '/ops/logs': { label: 'Logs', description: 'Log search and live tail.' },
    '/ops/traces': { label: 'Traces', description: 'Trace search and span waterfall.' },
    '/ops/alerts': { label: 'Alerts', description: 'Firing alerts, grouped, with incident creation.' },
    '/ops/incidents': { label: 'Incidents', description: 'Incident lifecycle.' },
    '/contacts': { label: 'Contacts', description: 'Registry contact objects (registrant, admin, tech, billing) and the domains using them.' },
    '/transfers': { label: 'Transfers', description: 'Inbound and outbound domain transfers and their state.' },
    '/registrant-changes': { label: 'Registrant Changes', description: 'Changes of registrant (trade) with approvals and locks.' },
    '/compliance/policies': { label: 'Policies', description: 'Versioned compliance policies and their effective dates.' },
    '/compliance/work': { label: 'Work Queue', description: 'Everything that needs a person: eKYC and dossier reviews, and operational exceptions.' },
    '/compliance/deadlines': { label: 'Deadlines', description: 'Regulatory deadlines (VNNIC, ICANN) and whether they are met.' },
    '/settings/registries': { label: 'Registry Connections', description: 'How we connect to each registry: EPP, registry REST API, or through an upstream registrar.' },
    '/settings/tlds': { label: 'TLD Channels', description: 'Which connection each TLD is registered through.' },
    '/settings/vnnic': { label: 'VNNIC Integrations', description: 'VNNIC Registrar API, eKYC, dossier (HSTM) and gTLD reporting API settings.' },
    '/settings/email': { label: 'E-mail & Templates', description: 'Sending settings, brand and e-mail templates; a customer whose tier allows it sets its own on its page.' },
    '/settings/tld-settings': { label: 'TLD Settings', description: 'Transfer-in, restore and expiry terms per TLD, and the parking name servers.' },
    '/domain-services': { label: 'Domain Services', description: 'Lifecycle and add-on services: catalogue, prices and tax categories.' },
    '/name-changes': { label: 'Name Changes', description: '.vn name changes and holder transfers: documents, review, VNNIC panel step.' },
    '/settings/name-change-requirements': { label: 'Name-change Documents', description: 'Documents required per name-change case (versioned requirement sets).' },
    '/compliance/protected': { label: 'Protected Domains', description: 'Protected domains needing action: pending changes and expiry stages.' },
    '/settings/webhooks': { label: 'Webhooks', description: 'Signed events sent to your systems (e.g. a CRM), with the delivery log.' },
    '/settings/email-log': { label: 'Sent E-mails', description: 'Every notice sent or being sent, with what was sent.' },
    '/admin/staff': { label: 'Staff', description: 'Staff accounts, MFA status and role assignments.' },
    '/admin/roles': { label: 'Roles & Permissions', description: 'Roles and the permissions each one grants.' },
    '/admin/approvals': { label: 'Approvals', description: 'Four-eyes approval requests awaiting a second operator.' },
    '/admin/api-credentials': { label: 'API Credentials', description: 'API credentials issued to customers.' },
    '/admin/audit': { label: 'Audit Log', description: 'Who did what, when and why, across the platform.' },
    '/ops/slo': { label: 'SLOs', description: 'Service level objectives and error budgets.' },
  },
  hubs: {
    home: {
      label: 'Work to do',
      description: 'Everything waiting for a person: domain dossiers to review before activation, work items, protected domains, approvals, deadlines and customer requests.',
      tabs: { dossiers: 'Domain dossiers', queue: 'Work queue', protected: 'Protected domains', approvals: 'Approvals', deadlines: 'Compliance deadlines', requests: 'Name change / transfer requests' },
      subs: {},
    },
    domains: {
      label: 'Domains',
      description: 'The domain portfolio and everything around it: transfers, registrant changes and contacts.',
      tabs: { list: 'All domains', lookup: 'Domain lookup', transfers: 'Transfers', 'registrant-changes': 'Registrant changes', contacts: 'Contacts' },
      subs: {},
    },
    customers: {
      label: 'Customers',
      description: 'Customer accounts and their tiers, the registrants behind domains, their dossiers and documents.',
      tabs: { customers: 'Customers', subjects: 'Subjects', dossiers: 'Dossiers', documents: 'Documents' },
      subs: {},
    },
    sales: {
      label: 'Sales & finance',
      description: 'Quotes, orders, invoices, payments and wallets.',
      tabs: { quotes: 'Quotes', orders: 'Orders', invoices: 'Invoices', payments: 'Payments', 'bank-transactions': 'Bank transactions', ledger: 'Ledger / wallets', subscriptions: 'Services', overview: 'Overview' },
      subs: {},
    },
    products: {
      label: 'Products & prices',
      description: 'What we sell and for how much: each TLD with its readiness, the product catalogue (hosting, cloud, email …), price lists, promotions, domain services and tax rates.',
      tabs: { tlds: 'TLDs', pricing: 'Price lists', promotions: 'Promotions', services: 'Add-ons & fees', tax: 'Tax rates', quote: 'Price calculator', catalog: 'TLD catalogue', 'product-catalog': 'Product catalogue' },
      subs: { 'product-catalog': { products: 'Products & plans', 'server-groups': 'Server groups', locations: 'Locations', 'service-quote': 'Service price calculator', 'price-rules': 'Tier and customer prices', 'promotion-scopes': 'Service promotions' } },
    },
    backorder: {
      label: 'Backorder',
      description: 'Names about to be released, the customers waiting for them, the catch at release time and the money held meanwhile.',
      tabs: { overview: 'Overview', drops: 'Drop list', requests: 'Requests', catching: 'Drop catching', holds: 'Holds & releases' },
      subs: {},
    },
    auctions: {
      label: 'Domain auctions',
      description: 'Auction sessions, running them, every bid, settlement and handing the name to the winner.',
      tabs: { market: 'Sessions', sessions: 'Create & run', bids: 'Bids & audit', settlement: 'Settlement', handover: 'Handover' },
      subs: {},
    },
    compliance: {
      label: 'Compliance',
      description: 'Reporting to the authorities (through VNNIC), ICANN obligations, abuse, disputes, legal locks and policies.',
      tabs: { vnnic: 'State reporting', icann: 'ICANN', cases: 'Cases', policies: 'Policies' },
      subs: {
        vnnic: { 'dossier-sync': 'Domain dossier submissions', 'international-reports': 'International domain reports', coverage: 'Coverage', corrections: 'Corrections needed', 'post-operations': 'After operations', 'registrar-signature': 'Awaiting registrar signature', 'tracking-reports': 'Daily tracking report' },
        icann: { rdap: 'RDAP', rde: 'RDE (escrow)', disclosure: 'Data disclosure' },
        cases: { inbox: 'All cases', abuse: 'Abuse', disputes: 'Disputes', 'legal-locks': 'Legal locks' },
      },
    },
    ops: {
      label: 'Registries & operations',
      description: 'Why something failed and whether the platform is healthy: registry commands, reconciliation, registry messages, workflows, alerts and monitoring.',
      tabs: { overview: 'Overview', registry: 'Registries', queues: 'Workflows & queues', alerts: 'Alerts & incidents', services: 'Services', telemetry: 'Monitoring data', slo: 'Service targets' },
      subs: {
        registry: { registries: 'Registries', transactions: 'Registry commands', reconciliation: 'Reconciliation', poll: 'Registry messages', sessions: 'Connection sessions', epp: 'Connection settings', health: 'Health' },
        queues: { queues: 'Queues', workflows: 'Workflows' },
        alerts: { alerts: 'Alerts', incidents: 'Incidents' },
        telemetry: { metrics: 'Metrics', logs: 'Logs', traces: 'Traces' },
      },
    },
    settings: {
      label: 'Settings',
      description: 'Every setting in one searchable list; each one opens its single place.',
      tabs: { all: 'All settings', domains: 'Domains & TLDs', connections: 'Connections', modules: 'Modules', notify: 'E-mail & notifications', security: 'Organisation & staff' },
      subs: {
        domains: { channels: 'TLD channels', lifecycle: 'Lifecycle & transfer / restore', parking: 'Parking DNS', requirements: 'Document sets', declarations: 'Declaration forms', 'document-types': 'Document types', 'trust-anchors': 'Trusted root certificates', 'declaration-checks': 'Declaration checks', 'vn-zones': '.vn zone rules' },
        connections: { registries: 'Registries & upstream registrars', vnnic: 'VNNIC', smartca: 'SmartCA', captcha: 'Anti-robot check', ote: 'OT&E tests', reporting: 'Reporting API' },
        notify: { email: 'E-mail & templates', sent: 'Sent e-mails', zalo: 'Zalo ZNS', webhooks: 'Event delivery (webhooks)' },
        security: { staff: 'Staff', roles: 'Roles & permissions', 'api-keys': 'Access keys', audit: 'Audit log', logs: 'Log retention' },
      },
    },
  },
  ui: {
    registerDomain: 'Register a domain for a customer',
    noAccess: 'Your account has no pages here yet.',
    quickActions: 'Quick actions',
    newCustomer: 'New customer',
    newSubject: 'New subject',
    goTo: 'Go to',
    actions: 'Actions',
    related: 'Related',
    results: 'Results',
    searching: 'Searching…',
    orderHit: 'Order · {{detail}}',
    invoiceHit: 'Invoice · {{detail}}',
    openOrderById: 'Open the order with this ID',
    openInvoiceById: 'Open the invoice with this ID',
    domainFilter: {
      label: 'Quick filters',
      all: 'All',
      active: 'Active',
      expiring30: 'Expiring in 30 days',
      expiring7: 'Expiring in 7 days',
      expired: 'Expired',
      grace: 'Awaiting renewal',
      redemption: 'Redemption',
      pendingDelete: 'Pending deletion',
      protected: 'Protected',
    },
    ote: {
      title: 'OT&E tests',
      subtitle: 'Registry certification tests, run from the OT&E server against each EPP connection.',
      open: 'Open tests',
      none: 'No EPP connections yet.',
      connection: 'Connection',
      protocol: 'Protocol',
      status: 'Status',
    },
    settingsLink: {
      title: 'Settings for this TLD',
      body: 'These settings are shared by every TLD and are edited in Settings; the same form is shown here.',
      open: 'Open in Settings',
    },
    registryLink: {
      connection: 'Connection settings',
      connectionHint: 'Credentials, certificates and addresses are edited in Settings › Connections.',
      ote: 'OT&E tests for this registry run from its connection page.',
      openTests: 'Open OT&E tests',
    },
  },
} as const;
